import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  Select,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

export function ConcreteRebarLayer() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [bw, setBw] = useState('')
  const [c, setC] = useState('')
  const [phiL, setPhiL] = useState('')
  const [phiE, setPhiE] = useState('')
  const [phiAgregado, setPhiAgregado] = useState(38)
  const [diameterUnit, setDiameterUnit] = useState('mm')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-rebar-layer-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setBw(entry.bw)
      setC(entry.c)
      setPhiL(entry.phiL)
      setPhiE(entry.phiE)
      setPhiAgregado(entry.phiAgregado)
      setDiameterUnit(entry.diameterUnit || 'mm')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.bw = validateField('bw', bw)
    newErrors.c = validateField('c', c)
    newErrors.phiL = validateField('phiL', phiL)
    newErrors.phiE = validateField('phiE', phiE)
    newErrors.phiAgregado = validateField('phiAgregado', phiAgregado)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [bw, c, phiL, phiE, phiAgregado, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const convertToStandardUnits = () => {
    return {
      bw,
      c,
      phiL,
      phiE,
      phiAgregado,
      diameterUnit,
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = convertToStandardUnits()

      setData(data)

      const response = await api.post('/concrete/rebar-layer', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        bw,
        c,
        phiL,
        phiE,
        phiAgregado,
        diameterUnit,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      bw,
      c,
      phiL,
      phiE,
      phiAgregado,
      diameterUnit,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Camada de Armadura.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setBw(jsonData.bw)
        setC(jsonData.c)
        setPhiL(jsonData.phiL)
        setPhiE(jsonData.phiE)
        setPhiAgregado(jsonData.phiAgregado)
        setDiameterUnit(jsonData.diameterUnit || 'mm')
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Camada de Armadura</span>
          </div>
          <h1>🔩 Distribuição de Barras em uma Camada</h1>
          <p className="subtitle">
            Calcule a quantidade máxima de barras longitudinais que cabem em uma única camada de uma viga
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Espaçamento entre barras</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta calcula a quantidade máxima de barras longitudinais que cabem em uma única camada de uma
              viga, considerando cobrimento, estribo, diâmetro da barra longitudinal e o espaçamento livre mínimo entre
              barras conforme a NBR 6118:2014.
            </p>
            <p>
              Ideal para verificar se a armadura calculada cabe em uma camada ou se é necessário distribuí-la em duas.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Espaçamento de Armadura</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura da viga bw (cm)</span>
              <ValidatedInput
                name="bw"
                type="number"
                value={bw}
                onChange={(event) => setBw(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Cobrimento c (cm)</span>
              <ValidatedInput
                name="c"
                type="number"
                value={c}
                onChange={(event) => setC(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Unidade dos diâmetros</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={diameterUnit}
                  onChange={(e) => setDiameterUnit(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="mm">mm</option>
                  <option value="cm">cm</option>
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Diâmetro da barra longitudinal Øl ({diameterUnit})</span>
              <ValidatedInput
                name="phiL"
                type="number"
                value={phiL}
                onChange={(event) => setPhiL(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Diâmetro do estribo Øe ({diameterUnit})</span>
              <ValidatedInput
                name="phiE"
                type="number"
                value={phiE}
                onChange={(event) => setPhiE(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Diâmetro máximo do agregado ({diameterUnit})</span>
              <ValidatedInput
                name="phiAgregado"
                type="number"
                value={phiAgregado}
                onChange={(event) => setPhiAgregado(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`\\boxed{n_{max}= ${result.maximumBars} \\text{ barras}}`} />
              </MathDiv>
              <MathDiv>
                <BlockMath math={`b_{si}= ${roundToDecimals(result.bsi)} \\text{ cm}`} />
                <BlockMath math={`d_{min}= ${roundToDecimals(result.spacing.minimumClear)} \\text{ cm}`} />
                {result.maximumBars > 1 && (
                  <BlockMath
                    math={`s= ${roundToDecimals(result.distributedClearSpacing)} \\text{ cm (espaçamento distribuído)}`}
                  />
                )}
              </MathDiv>
              {!result.hasAvailableWidth && (
                <MathDiv>
                  <BlockMath math={`\\text{Nenhuma barra cabe na camada com os dados informados.}`} />
                </MathDiv>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Espaço livre entre estribos</h3>
              <MathDiv>
                <BlockMath math={`b_{si}= b_w - 2 \\cdot (c + \\phi_e)`} />
                <BlockMath
                  math={`b_{si}= ${data.bw} - 2 \\cdot (${data.c} + ${roundToDecimals(result.phiECm)}) = ${roundToDecimals(result.bsi)} \\text{ cm}`}
                />
              </MathDiv>
              <h3>Espaçamento livre mínimo entre barras</h3>
              <MathDiv>
                <BlockMath>
                  {String.raw`d \geq \begin{cases} 2 \text{ cm} \\ 1.2 \cdot \phi_{agregado} = 1.2 \cdot ${roundToDecimals(result.phiAgregadoCm)} = ${roundToDecimals(result.spacing.byAggregate)} \text{ cm} \\ \phi_l = ${roundToDecimals(result.spacing.byBarDiameter)} \text{ cm} \end{cases}`}
                </BlockMath>
                <BlockMath math={`\\boxed{d_{min}= ${roundToDecimals(result.spacing.minimumClear)} \\text{ cm}}`} />
              </MathDiv>
              <h3>Quantidade máxima de barras</h3>
              <MathDiv>
                <BlockMath
                  math={`n_{max}= \\left\\lfloor \\dfrac{b_{si} + d_{min}}{\\phi_l + d_{min}} \\right\\rfloor = \\left\\lfloor \\dfrac{${roundToDecimals(result.bsi)} + ${roundToDecimals(result.spacing.minimumClear)}}{${roundToDecimals(result.phiLCm)} + ${roundToDecimals(result.spacing.minimumClear)}} \\right\\rfloor`}
                />
                <BlockMath math={`\\boxed{n_{max}= ${result.maximumBars} \\text{ barras}}`} />
              </MathDiv>
              {result.maximumBars > 0 && (
                <>
                  <h3>Largura mínima ocupada</h3>
                  <MathDiv>
                    <BlockMath math={`w_{min}= n_{max} \\cdot \\phi_l + (n_{max}-1) \\cdot d_{min}`} />
                    <BlockMath
                      math={`w_{min}= ${result.maximumBars} \\cdot ${roundToDecimals(result.phiLCm)} + (${result.maximumBars}-1) \\cdot ${roundToDecimals(result.spacing.minimumClear)} = ${roundToDecimals(result.minimumOccupiedWidth)} \\text{ cm}`}
                    />
                  </MathDiv>
                  <h3>Sobra de largura</h3>
                  <MathDiv>
                    <BlockMath math={`w_{sobra}= b_{si} - w_{min}`} />
                    <BlockMath
                      math={`w_{sobra}= ${roundToDecimals(result.bsi)} - ${roundToDecimals(result.minimumOccupiedWidth)} = ${roundToDecimals(result.remainingWidth)} \\text{ cm}`}
                    />
                  </MathDiv>
                </>
              )}
              {result.maximumBars > 1 && (
                <>
                  <h3>Espaçamento distribuído igualmente</h3>
                  <MathDiv>
                    <BlockMath math={`s= \\dfrac{b_{si} - n_{max} \\cdot \\phi_l}{n_{max} - 1}`} />
                    <BlockMath
                      math={`s= \\dfrac{${roundToDecimals(result.bsi)} - ${result.maximumBars} \\cdot ${roundToDecimals(result.phiLCm)}}{${result.maximumBars} - 1} = ${roundToDecimals(result.distributedClearSpacing)} \\text{ cm}`}
                    />
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
